import { loginRequest } from '@/authConfig';
import ProtectedRoute from '@/common/components/shared/protected-route/ProtectedRoute';
import { msalInstance } from '@/common/context-providers/AppProvider';
import { getUserAccessToken, isTokenExpired } from '@/common/utils/auth';
import { useMsal } from '@azure/msal-react';
import { FC, PropsWithChildren, useState, useCallback, useEffect } from 'react';

interface ErrorBoundaryProps extends PropsWithChildren {
  fallback?: React.ReactNode;
}

const ErrorBoundary: FC<ErrorBoundaryProps> = ({ children, fallback }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { instance } = useMsal();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const getToken = async () => {
      try {
        const accessToken = await getUserAccessToken(msalInstance);
        setToken(accessToken);
      } catch (error) {
        console.error('Error getting token:', error);
        setToken(null);
      }
    };
    
    getToken();
  }, []);

  const handleError = useCallback((error: Error) => {
    setError(error);
    setHasError(true);
    
    console.error('Uncaught error:', error);
  }, []);

  const handleLoginAndReload = async () => {
    try {
      await instance.loginRedirect(loginRequest);
  
      window.location.reload();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogoutAndReload = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });

      sessionStorage.clear();
      localStorage.clear();

      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const ErrorFallback = () => (
    <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
      <h1 className="text-danger mb-3">Something went wrong</h1>
      <p className="text-800">{error?.message}</p>
      <div className="d-flex gap-2">
        {!token || (token && isTokenExpired(token)) ? (
          <button
            className="btn btn-primary"
            onClick={handleLoginAndReload}
          >
            Login and Reload
          </button>
        ) : (
          <button
            className="btn btn-warning"
            onClick={handleLogoutAndReload}
          >
            Logout and Reload
          </button>
        )}
        <button
          className="btn btn-secondary"
          onClick={() => {
            setHasError(false);
            setError(null);
            window.location.reload();
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );

  if (hasError) {
    return <>{fallback || <ErrorFallback />}</>;
  }

  window.onerror = (message, source, lineno, colno, error) => {
    if (error) handleError(error);
    return false;
  };

  window.onunhandledrejection = (event) => {
    handleError(event.reason);
  };

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

export default ErrorBoundary; 